import { useTheme } from "next-themes"
import { Toaster as Sonner, toast } from "sonner"
import { useCart } from "@/hooks/useCart"

type ToasterProps = React.ComponentProps<typeof Sonner>

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()
  const { isCartOpen } = useCart()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      position="top-right"
      closeButton={true}
      gap={8}
      style={{
        transform: isCartOpen ? "translateX(-448px)" : "translateX(0)",
        transition: "transform 0.3s ease",
        zIndex: 110,
      }}
      toastOptions={{
        classNames: {
          // Base card: white in light, dark navy in dark - no border radius since our theme is 0px
          toast: [
            "group toast",
            "group-[.toaster]:flex group-[.toaster]:items-start group-[.toaster]:gap-3",
            "group-[.toaster]:bg-card group-[.toaster]:text-card-foreground",
            "group-[.toaster]:border group-[.toaster]:border-border",
            "group-[.toaster]:shadow-md",
            "group-[.toaster]:p-4",
            "group-[.toaster]:rounded-none",
            "group-[.toaster]:min-w-[320px] group-[.toaster]:max-w-[400px]",
          ].join(" "),

          // Title: semi-bold, normal case - no uppercase
          title: [
            "group-[.toast]:font-semibold",
            "group-[.toast]:text-sm",
            "group-[.toast]:text-card-foreground",
            "group-[.toast]:leading-snug",
          ].join(" "),

          // Description: muted, slightly smaller
          description: [
            "group-[.toast]:text-muted-foreground",
            "group-[.toast]:text-xs",
            "group-[.toast]:mt-0.5",
            "group-[.toast]:leading-relaxed",
          ].join(" "),

          // Action button - amber/warning tone, like the "UNDO" in the reference
          actionButton: [
            "group-[.toast]:bg-transparent",
            "group-[.toast]:text-warning",
            "group-[.toast]:font-bold",
            "group-[.toast]:text-xs",
            "group-[.toast]:uppercase",
            "group-[.toast]:tracking-widest",
            "group-[.toast]:px-0",
            "group-[.toast]:hover:text-warning/80",
          ].join(" "),

          // Cancel button - subtle
          cancelButton: [
            "group-[.toast]:bg-muted",
            "group-[.toast]:text-muted-foreground",
            "group-[.toast]:text-xs",
            "group-[.toast]:font-medium",
          ].join(" "),

          // Close button
          closeButton: [
            "group-[.toaster]:border group-[.toaster]:border-border",
            "group-[.toaster]:bg-card",
            "group-[.toaster]:text-muted-foreground",
            "group-[.toaster]:hover:text-foreground",
            "group-[.toaster]:hover:bg-muted",
          ].join(" "),

          // Success - green left accent + icon ring
          success: [
            "group-[.toaster]:border-l-4 group-[.toaster]:border-l-success",
            "group-[.toaster]:border-t-border group-[.toaster]:border-r-border group-[.toaster]:border-b-border",
            "[&>[data-icon]]:bg-success/10 [&>[data-icon]]:text-success [&>[data-icon]]:rounded-full [&>[data-icon]]:p-1",
          ].join(" "),

          // Error - red left accent
          error: [
            "group-[.toaster]:border-l-4 group-[.toaster]:border-l-destructive",
            "group-[.toaster]:border-t-border group-[.toaster]:border-r-border group-[.toaster]:border-b-border",
            "[&>[data-icon]]:bg-destructive/10 [&>[data-icon]]:text-destructive [&>[data-icon]]:rounded-full [&>[data-icon]]:p-1",
          ].join(" "),

          // Info - blue left accent
          info: [
            "group-[.toaster]:border-l-4 group-[.toaster]:border-l-info",
            "group-[.toaster]:border-t-border group-[.toaster]:border-r-border group-[.toaster]:border-b-border",
            "[&>[data-icon]]:bg-info/10 [&>[data-icon]]:text-info [&>[data-icon]]:rounded-full [&>[data-icon]]:p-1",
          ].join(" "),

          // Warning - amber left accent
          warning: [
            "group-[.toaster]:border-l-4 group-[.toaster]:border-l-warning",
            "group-[.toaster]:border-t-border group-[.toaster]:border-r-border group-[.toaster]:border-b-border",
            "[&>[data-icon]]:bg-warning/10 [&>[data-icon]]:text-warning [&>[data-icon]]:rounded-full [&>[data-icon]]:p-1",
          ].join(" "),

          // Loader spinner color
          loader: "group-[.toast]:text-primary",
        },
      }}
      {...props}
    />
  )
}

export { Toaster, toast }

